import "./customer-search.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { xClose } from "@tql/ui-components-react/untitled/icons";
import type { CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";

import { getSearchCustomerRoute } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { SearchCombobox } from "~/shared/components/fields/search-combobox.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { customerDetailsSchema } from "~/shared/schemas/customer-schema.ts";
import type { Customer } from "~/shared/types/quote-details-types";

type CustomerSearchProps = {
	selectedCustomer: Customer | undefined;
	onCustomerChange: (customer: Customer | null) => void;
	placeholder?: string;
	wrapperStyle?: CSSProperties;
	disabled?: boolean;
};

export function CustomerSearch({
	selectedCustomer,
	onCustomerChange,
	placeholder = "Search for a customer...",
	wrapperStyle,
	disabled = false,
}: Readonly<CustomerSearchProps>) {
	const { isInternal } = useDeploymentType();
	const navigate = useNavigate();
	const location = useLocation();

	if (!selectedCustomer && !isInternal) {
		return null;
	}

	return (
		<div className={"customer-search-wrapper"}>
			{selectedCustomer ? (
				<div
					className={"selected-customer-display"}
					data-testid={"selected-customer-display"}
				>
					<div className={"customer-title"}>
						{selectedCustomer.name} ({selectedCustomer.id})
					</div>
					{isInternal ? (
						<Button
							aria-label={"Clear selected customer"}
							data-testid={"clear-selected-customer-button"}
							disabled={disabled}
							onClick={() => {
								onCustomerChange(null);
								const [basePath] = location.pathname.split(
									`/${selectedCustomer.id}`,
								);
								navigate(basePath || "/");
							}}
							svgIcon={xClose}
							variant={"tertiary"}
						/>
					) : null}
				</div>
			) : (
				<SearchCombobox
					dataItemKey={"id"}
					disabled={disabled}
					handleChange={(event) => {
						onCustomerChange(event.value as Customer);
					}}
					hint={false}
					hintDirection={undefined}
					id={"customer"}
					label={""}
					minSearchLength={3}
					name={"customer"}
					placeholder={placeholder}
					route={getSearchCustomerRoute}
					schema={customerDetailsSchema}
					textField={"nameWithId"}
					value={selectedCustomer}
					wrapperStyle={wrapperStyle}
				/>
			)}
		</div>
	);
}
